/**
 * Client wrapper for the `request-help` Edge Function (CML-183).
 *
 * Mirrors the direct-fetch pattern used by `callAdminUserAction` /
 * `UserManagement` for `create-user`: we bypass `supabase.functions.invoke`
 * so error bodies are readable, attach the caller's access token, and send the
 * anon key as the `apikey` header.
 *
 * The function verifies the JWT server-side, reads the caller email from it
 * (used as the email reply-to), emails a support ticket via Resend, and
 * best-effort files a Linear issue.
 */
import { supabase } from '@/lib/supabase';
import { SUPABASE_PUBLISHABLE_KEY } from '@/lib/supabaseKey';

/** A single file attachment, base64-encoded without the `data:` URI prefix. */
export interface RequestHelpAttachment {
  filename: string;
  contentType: string;
  /** Base64-encoded file contents. */
  data: string;
}

export interface RequestHelpPayload {
  /** Required free-text message. */
  message: string;
  /** The page the user was on (window.location.href). */
  page_url: string;
  /** Optional display name captured from the user's profile metadata. */
  display_name?: string;
  /** Optional account/workspace name. */
  account_name?: string;
  /** Optional file attachments (validated again server-side). */
  attachments?: RequestHelpAttachment[];
}

export interface RequestHelpResult {
  success: boolean;
  /** Present only when the best-effort Linear issue was created. */
  linear_issue_url?: string;
}

export async function requestHelp(
  payload: RequestHelpPayload,
): Promise<RequestHelpResult> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  if (!token) {
    throw new Error('Not authenticated. Please sign in again.');
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = SUPABASE_PUBLISHABLE_KEY;

  const response = await fetch(`${supabaseUrl}/functions/v1/request-help`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      apikey: anonKey,
    },
    body: JSON.stringify(payload),
  });

  let data: Record<string, unknown> = {};
  try {
    data = await response.json();
  } catch {
    // Non-JSON response — fall through to the generic error below.
  }

  if (!response.ok || data.error) {
    const err = data.error as { message?: string } | string | undefined;
    const message =
      typeof err === 'string'
        ? err
        : err?.message || `Help request failed (HTTP ${response.status})`;
    throw new Error(message);
  }

  return data as unknown as RequestHelpResult;
}
